import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSignalRContext } from '../components/Contexts/SignalRContext'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { offlineDb, type MutationQueueItem } from './db'
import { beginSync, endSync, reportSynced } from './syncStatus'
import { store } from '../state/store'
import { clearProvisionalShift } from '../state/reducers/shiftReducer'
import { refreshLatestShift } from './refreshLatestShift'
import { patchCachedEntityId, patchCachedRowVersion } from './patchCachedRowVersion'
import { syncAjax } from '../ajax/SyncAjax'
import { SyncOperation, SyncStatus } from '../DTOs/Sync/SyncDtos'
import type { OperationMeterMutation, SyncRequest, SyncResponse } from '../DTOs/Sync/SyncDtos'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { recordSyncFailure, uploadSyncFailures } from './syncFailures'
import { isOnlineNow, subscribeToReachability } from './connectivity'
import { refreshMachineLists } from './refreshMachineLists'

function operationCode(op: MutationQueueItem['operation']) {
  if (op === 'create') return SyncOperation.Create
  if (op === 'update') return SyncOperation.Update
  return SyncOperation.Delete
}

// Note: a 401 says the session could not be renewed, not that the change was wrong. The queue survives
// sign-out, so leaving the change queued lets it sync once the user signs back in.
function isSessionFailure(code?: number) {
  return code === 401
}

const SERVER_ERROR_RETRY_MS = 7 * 24 * 60 * 60 * 1000

// Note: 0 is no reply at all, the network gone or the request timed out. It goes through keep like any other
// failure, so an outage is logged once per change instead of showing only as a queue that will not drain.
const NO_REPLY = 0
const NO_REPLY_MESSAGE = 'No reply from the server: the network dropped or the request timed out.'

function isServerError(code?: number) {
  return code !== undefined && code >= 500
}

type SyncAttemptMode = 'batch' | 'single'

const CONSUMABLE_KEY = /^(consumable|drillComponent|fittableComponents|componentLife|parkedConsumable)/

function buildSyncRequest(items: MutationQueueItem[]): SyncRequest {
  const request: SyncRequest = {
    stoppages: [], loadings: [], diggingPatterns: [], completeShifts: [], geoRehandles: [], operationLocations: [], engineHours: [],
    drillingSessions: [], drillingPatterns: [], drillingLines: [], operationMeters: [], gcLines: [], drilledHoles: [], holeDips: [], holeCharges: [], explosiveMovements: [], firings: [],
    misfires: [], consumableMovements: [], componentActions: [],
  }
  for (const item of items) {
    const base = {
      clientId: item.clientId,
      operation: operationCode(item.operation),
      shiftId: item.shiftId,
      rowVersion: item.rowVersion,
      data: convertDatesToUTC(item.data),
    }
    if (item.entity === 'stoppage') {
      const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...safeData } = (base.data ?? {}) as any
      request.stoppages.push({ ...base, data: safeData, stoppageId: item.entityId })
    } else if (item.entity === 'loading') {
      request.loadings.push({ ...base, loadingId: item.entityId })
    } else if (item.entity === 'diggingPattern') {
      request.diggingPatterns.push({ ...base, diggingPatternId: item.entityId })
    } else if (item.entity === 'completeShift') {
      request.completeShifts.push({ clientId: item.clientId, shiftId: item.shiftId })
    } else if (item.entity === 'operationLocation') {
      const d = (item.data ?? {}) as { operationId?: number; locationId?: number; operationPlantId?: number }
      request.operationLocations.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        locationId: d.locationId ?? 0,
        shiftId: item.shiftId,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'engineHours') {
      const d = (item.data ?? {}) as { operationId?: number; startEngineHours?: number; endEngineHours?: number; fuel?: number | null; operationPlantId?: number }
      request.engineHours.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        shiftId: item.shiftId,
        startEngineHours: d.startEngineHours,
        endEngineHours: d.endEngineHours,
        fuel: d.fuel ?? undefined,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'geoRehandle') {
      request.geoRehandles.push({ clientId: item.clientId, operation: operationCode(item.operation), geoRehandleId: item.entityId, rowVersion: item.rowVersion, shiftId: item.shiftId, data: item.data })
    } else if (item.entity === 'drillingSession') {
      const d = (item.data ?? {}) as { rigId: number; drillingTypeId: number }
      request.drillingSessions.push({ clientId: item.clientId, shiftId: item.shiftId, rigId: d.rigId, drillingTypeId: d.drillingTypeId, drillingId: item.entityId })
    } else if (item.entity === 'drillingPattern') {
      const d = (item.data ?? {}) as { drillingId: number; patternBlastId: number; entryMode?: number }
      request.drillingPatterns.push({
        clientId: item.clientId,
        operation: operationCode(item.operation),
        shiftId: item.shiftId,
        drillingBlastId: item.entityId,
        drillingId: d.drillingId,
        patternBlastId: d.patternBlastId,
        entryMode: d.entryMode ?? 0,
      })
    } else if (item.entity === 'drillingLine') {
      request.drillingLines.push({ clientId: item.clientId, operation: operationCode(item.operation), shiftId: item.shiftId, lineId: item.entityId, rowVersion: item.rowVersion, data: base.data })
    } else if (item.entity === 'operationMeter') {
      request.operationMeters.push({ ...(base.data as Omit<OperationMeterMutation, 'clientId' | 'shiftId'>), clientId: item.clientId, shiftId: item.shiftId })
    } else if (item.entity === 'gcLine') {
      request.gcLines.push({ clientId: item.clientId, operation: operationCode(item.operation), shiftId: item.shiftId, lineId: item.entityId, rowVersion: item.rowVersion, data: base.data })
    } else if (item.entity === 'drilledHole') {
      request.drilledHoles.push({ clientId: item.clientId, operation: operationCode(item.operation), shiftId: item.shiftId, attemptId: item.entityId, rowVersion: item.rowVersion, data: base.data })
    } else if (item.entity === 'holeDip') {
      request.holeDips.push({ clientId: item.clientId, operation: operationCode(item.operation), dipId: item.entityId, rowVersion: item.rowVersion, data: base.data })
    } else if (item.entity === 'holeCharge') {
      request.holeCharges.push({ clientId: item.clientId, blastHoleId: item.entityId ?? 0, data: base.data })
    } else if (item.entity === 'explosiveMovement') {
      request.explosiveMovements.push({ clientId: item.clientId, data: base.data })
    } else if (item.entity === 'firing') {
      const d = (base.data ?? {}) as { patternBlastId: number; firing: unknown }
      request.firings.push({
        clientId: item.clientId,
        operation: operationCode(item.operation),
        patternBlastId: d.patternBlastId,
        firingId: item.entityId,
        rowVersion: item.rowVersion,
        data: d.firing,
      })
    } else if (item.entity === 'misfire') {
      const d = (base.data ?? {}) as { patternBlastId: number; misfire: unknown }
      request.misfires.push({ clientId: item.clientId, patternBlastId: d.patternBlastId, data: d.misfire })
    } else if (item.entity === 'consumableMovement') {
      request.consumableMovements.push({ clientId: item.clientId, data: base.data })
    } else if (item.entity === 'componentAction') {
      const d = (base.data ?? {}) as { fit?: unknown; remove?: unknown }
      request.componentActions.push({ clientId: item.clientId, fit: d.fit, remove: d.remove })
    }
  }
  return request
}

// Note: a change to a row whose create is still queued waits for that create to sync and swap its temp id for
// the real one. Sent in the same request it would still carry the temp id, which the server cannot find.
function waitsForCreate(item: MutationQueueItem, queue: MutationQueueItem[]): boolean {
  return item.operation !== 'create'
    && typeof item.entityId === 'number'
    && item.entityId < 0
    && queue.some(other => other.operation === 'create' && other.entity === item.entity && other.entityId === item.entityId)
}

async function waitsForPattern(item: MutationQueueItem): Promise<boolean> {
  const patternId = (item.data as { diggingPatternId?: number } | undefined)?.diggingPatternId
  if (item.entity !== 'loading' || typeof patternId !== 'number' || patternId >= 0) return false
  return await offlineDb.mutationQueue.where('[entity+entityId]').equals(['diggingPattern', patternId]).count() > 0
}

async function remapQueuedPatternId(tempId: number, realId: number): Promise<void> {
  const queued = await offlineDb.mutationQueue.where('entity').equals('loading').toArray()

  for (const item of queued) {
    const data = item.data as { diggingPatternId?: number } | undefined
    if (!data || data.diggingPatternId !== tempId) continue
    await offlineDb.mutationQueue.update(item.id!, { data: { ...data, diggingPatternId: realId } })
  }
}

// Note: a child queued against its parent's temp id, while the parent was itself still queued, is pointed at the parent's real id.
const CHILD_REFERENCES: Partial<Record<MutationQueueItem['entity'], { entity: MutationQueueItem['entity']; field: string }[]>> = {
  drillingSession: [{ entity: 'drillingPattern', field: 'drillingId' }, { entity: 'gcLine', field: 'drillingId' }],
  drillingPattern: [{ entity: 'drillingLine', field: 'drillingBlastId' }, { entity: 'drilledHole', field: 'drillingBlastId' }],
}

async function waitsForParent(item: MutationQueueItem): Promise<boolean> {
  const data = item.data as Record<string, unknown> | undefined
  if (!data) return false

  for (const [parent, children] of Object.entries(CHILD_REFERENCES)) {
    for (const child of children ?? []) {
      const parentId = data[child.field]
      if (child.entity !== item.entity || typeof parentId !== 'number' || parentId >= 0) continue
      if (await offlineDb.mutationQueue.where('[entity+entityId]').equals([parent, parentId]).count() > 0) return true
    }
  }
  return false
}

async function remapQueuedChildren(entity: MutationQueueItem['entity'], tempId: number, realId: number): Promise<void> {
  for (const child of CHILD_REFERENCES[entity] ?? []) {
    const queued = await offlineDb.mutationQueue.where('entity').equals(child.entity).toArray()

    for (const item of queued) {
      const data = item.data as Record<string, unknown> | undefined
      if (!data || data[child.field] !== tempId) continue
      await offlineDb.mutationQueue.update(item.id!, { data: { ...data, [child.field]: realId } })
    }
  }
}

async function remapQueuedEntityId(
  entity: MutationQueueItem['entity'],
  tempId: number,
  realId: number,
): Promise<void> {
  const queued = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, tempId])
    .toArray()

  for (const item of queued) {
    const data = item.data && typeof item.data === 'object'
      ? { ...(item.data as Record<string, unknown>) }
      : item.data

    if (data && typeof data === 'object') {
      for (const idField of ['loadingId', 'stoppageId', 'geoRehandleId', 'diggingPatternId']) {
        if ((data as Record<string, unknown>)[idField] === tempId) {
          (data as Record<string, unknown>)[idField] = realId
        }
      }
    }

    await offlineDb.mutationQueue.update(item.id!, { entityId: realId, data })
  }
}

async function refreshStaleQueries(queryClient: QueryClient, staleEntities: Set<MutationQueueItem['entity']>) {
  try {
    for (const entity of staleEntities) {
      await refreshMachineLists(queryClient, entity)
      if (entity === 'stoppage') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'stoppages'] })
      } else if (entity === 'loading') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'truckLoads'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
      } else if (entity === 'diggingPattern') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
      } else if (entity === 'operationLocation' || entity === 'engineHours') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
        await queryClient.invalidateQueries({ queryKey: ['operations'] })
      } else if (entity === 'geoRehandle') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandles'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'crushing'] })
      } else if (entity === 'drillingSession' || entity === 'drillingPattern' || entity === 'drillingLine') {
        await queryClient.invalidateQueries({ queryKey: ['drilling'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'drilling'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'rcSession'] })
      } else if (entity === 'operationMeter') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'rigMeters'] })
      } else if (entity === 'gcLine') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'rcSession'] })
      } else if (entity === 'drilledHole') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'drillingSessionHoles'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'patternHoles'] })
        await queryClient.invalidateQueries({ queryKey: ['drilling'] })
      } else if (entity === 'holeDip') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'patternDips'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'patternHoles'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'pattern'] })
      } else if (entity === 'holeCharge') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'patternCharging'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'patternHoles'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'pattern'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'references', 'blastMaterialUsage'] })
      } else if (entity === 'firing' || entity === 'misfire') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'firing'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'pattern'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'openMisfires'] })
      } else if (entity === 'explosiveMovement') {
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'explosivesStock'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'explosivesTransactions'] })
        await queryClient.invalidateQueries({ queryKey: ['drillBlast', 'parkedMovements'] })
      } else if (entity === 'consumableMovement' || entity === 'componentAction') {
        await queryClient.invalidateQueries({ predicate: q => q.queryKey[0] === 'drillBlast' && CONSUMABLE_KEY.test(String(q.queryKey[1])) })
      } else if (entity === 'completeShift') {
        store.dispatch(clearProvisionalShift())
        await refreshLatestShift()
        await queryClient.invalidateQueries({ queryKey: ['dataTable'] })
        await queryClient.invalidateQueries({ queryKey: ['chatree'] })
        await queryClient.invalidateQueries({ queryKey: ['production'] })
        await queryClient.invalidateQueries({ queryKey: ['geology'] })
        await queryClient.invalidateQueries({ queryKey: ['shiftExistence'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
        await queryClient.invalidateQueries({ queryKey: ['operations'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'production'] })
        await queryClient.invalidateQueries({ queryKey: ['production'] })
      } else {
        await queryClient.invalidateQueries({ queryKey: ['references', 'blastmasterIds'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
      }
    }
  } catch (error) {
    console.error('Refreshing after a sync failed', error)
  }
}

export function useSyncWorker() {
  const queryClient = useQueryClient()
  const { connection } = useSignalRContext()
  const pendingCount = useLiveQuery(() => offlineDb.mutationQueue.count(), []) ?? 0
  const isSyncingRef = useRef(false)

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    try {
      const queue = await offlineDb.mutationQueue.orderBy('createdAt').toArray()
      const snapshot = queue.filter(item => !waitsForCreate(item, queue))

      if (snapshot.length) beginSync(snapshot.length)

      if (navigator.onLine
        && store.getState().shift.provisionalShiftDate
        && !queue.some(item => item.entity === 'completeShift')) {
        store.dispatch(clearProvisionalShift())
      }

      if (snapshot.length === 0) return

      const staleEntities = new Set<MutationQueueItem['entity']>()

      const processResults = async (results: SyncResponse['results'], items: MutationQueueItem[]) => {
        reportSynced(results.length)
        const byClientId = new Map(items.map(i => [i.clientId, i]))
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          for (const result of results) {
            const item = byClientId.get(result.clientId)
            if (!item) continue
            staleEntities.add(item.entity)

            if (item.operation === 'create'
              && result.status === SyncStatus.Applied
              && typeof result.serverId === 'number'
              && typeof item.entityId === 'number'
              && item.entityId < 0) {
              if (item.entity === 'diggingPattern') {
                await remapQueuedPatternId(item.entityId, result.serverId)
              }
              await remapQueuedChildren(item.entity, item.entityId, result.serverId)
              await remapQueuedEntityId(item.entity, item.entityId, result.serverId)
              patchCachedEntityId(item.entity, item.entityId, result.serverId)
            }

            if (item.operation === 'update' && result.status === SyncStatus.Applied) {
              patchCachedRowVersion(item.entity, item.entityId, result.serverRowVersion)
            }
            if (result.status !== SyncStatus.Applied) {
              await offlineDb.syncIssues.add({
                clientId: result.clientId,
                entity: item.entity,
                operation: item.operation,
                status: result.status,
                entityId: item.entityId,
                shiftId: item.shiftId,
                serverState: result.serverState,
                clientData: item.data,
                createdAt: Date.now(),
              })
            }
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          }
        })
      }

      const quarantine = async (item: MutationQueueItem, code?: number, reason?: string) => {
        staleEntities.add(item.entity)
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          await offlineDb.syncIssues.add({
            clientId: item.clientId,
            entity: item.entity,
            operation: item.operation,
            status: SyncStatus.Error,
            entityId: item.entityId,
            shiftId: item.shiftId,
            serverState: reason ?? `The server rejected this change${code ? ` (HTTP ${code})` : ''}, so it could not be applied. It has been removed from the queue - please re-enter it.`,
            clientData: item.data,
            createdAt: Date.now(),
          })
          await offlineDb.mutationQueue.where('clientId').equals(item.clientId).delete()
        })
      }

      // Note: a kept change is retried every 15 seconds, so it is logged when its failure changes,
      // not on every retry.
      const keep = async (item: MutationQueueItem, code: number | undefined, message: string | undefined, mode: SyncAttemptMode) => {
        const serverErrorSince = isServerError(code) ? item.serverErrorSince ?? Date.now() : undefined
        if (item.lastFailureCode === code && item.serverErrorSince === serverErrorSince) return
        await offlineDb.mutationQueue.update(item.id!, { lastFailureCode: code, serverErrorSince })
        await recordSyncFailure({ ...item, serverErrorSince }, code, message, 'Kept', { mode, batchSize: snapshot.length })
      }

      const handleFailure = async (item: MutationQueueItem, errors: ActionResponse<unknown>['errors'], mode: SyncAttemptMode) => {
        const code = errors?.[0]?.code
        const message = errors?.[0]?.description
        if (isSessionFailure(code)) {
          await keep(item, code, message, mode)
          return 'stop'
        }
        if (isServerError(code) && Date.now() - (item.serverErrorSince ?? Date.now()) < SERVER_ERROR_RETRY_MS) {
          await keep(item, code, message, mode)
          return 'continue'
        }
        const reason = isServerError(code)
          ? `The server kept failing (HTTP ${code}) for 7 days, so this change could not be applied. It has been removed from the queue - please re-enter it.`
          : undefined
        await quarantine(item, code, reason)
        await recordSyncFailure(item, code, message, 'Removed', { mode, batchSize: snapshot.length })
        return 'continue'
      }

      try {
        const response = await syncAjax.sync(buildSyncRequest(snapshot)).catch(() => null)
        if (response === null) {
          for (const item of snapshot) await keep(item, NO_REPLY, NO_REPLY_MESSAGE, 'batch')
          return
        }
        if (response.success && response.data) {
          await processResults(response.data.results, snapshot)
        } else if (isSessionFailure(response.errors?.[0]?.code)) {
          for (const item of snapshot) await keep(item, 401, response.errors?.[0]?.description, 'batch')
          return
        } else if (snapshot.length === 1) {
          await handleFailure(snapshot[0], response.errors, 'batch')
        } else {
          for (const item of snapshot) {
            const current = await offlineDb.mutationQueue.get(item.id!)
            if (!current || await waitsForPattern(current) || await waitsForParent(current)) continue
            const single = await syncAjax.sync(buildSyncRequest([current])).catch(() => null)
            if (single === null) {
              await keep(current, NO_REPLY, NO_REPLY_MESSAGE, 'single')
              return
            }
            if (single.success && single.data) {
              await processResults(single.data.results, [current])
            } else if (await handleFailure(current, single.errors, 'single') === 'stop') {
              return
            }
          }
        }
      } catch {
        return
      }

      // Note: not awaited, so the lock is released first. These refetches retry, and pause while the screen is
      // hidden, so awaiting them held the lock long after the changes themselves had synced.
      void refreshStaleQueries(queryClient, staleEntities)
    } finally {
      isSyncingRef.current = false
      endSync()
      void uploadSyncFailures()
    }
  }, [queryClient])

  useEffect(() => {
    let active = true
    const trigger = () => { if (active && isOnlineNow()) runSync() }

    trigger()
    const unsubscribe = subscribeToReachability(trigger)
    connection?.onreconnected(() => { if (active) runSync() })

    const retry = pendingCount > 0 ? setInterval(trigger, 15000) : null

    return () => {
      active = false
      unsubscribe()
      if (retry) clearInterval(retry)
    }
  }, [connection, runSync, pendingCount])
}
